import { env } from '$env/dynamic/public';
import { readConsentCookie } from '$lib/consent';

type PixelFunction = ((...args: unknown[]) => void) & {
	callMethod?: (...args: unknown[]) => void;
	queue: unknown[][];
	push?: PixelFunction;
	loaded: boolean;
	version: string;
};

declare global {
	interface Window {
		fbq?: PixelFunction;
		_fbq?: PixelFunction;
	}
}

let lastPage = '';

export function marketingAllowed(): boolean {
	try {
		return (
			readConsentCookie()?.advertising === true &&
			localStorage.getItem('plausible_ignore') !== 'true'
		);
	} catch {
		return false;
	}
}

export function loadMarketing(): void {
	const pixelId = env.PUBLIC_META_PIXEL_ID;
	if (!marketingAllowed() || !/^\d+$/.test(pixelId ?? '')) return;
	const clickId = new URL(location.href).searchParams.get('fbclid');
	const existingClick = document.cookie.match(/(?:^|;\s*)_fbc=([^;]*)/)?.[1];
	if (
		clickId &&
		/^[A-Za-z0-9_-]{1,500}$/.test(clickId) &&
		!existingClick?.endsWith(`.${clickId}`)
	) {
		document.cookie = `_fbc=fb.1.${Date.now()}.${clickId}; path=/; max-age=7776000; samesite=lax${location.protocol === 'https:' ? '; secure' : ''}`;
	}
	if (!window.fbq) {
		const pixel: PixelFunction = Object.assign(
			(...args: unknown[]) => {
				if (pixel.callMethod) pixel.callMethod(...args);
				else pixel.queue.push(args);
			},
			{ queue: [] as unknown[][], loaded: true, version: '2.0' }
		);
		pixel.push = pixel;
		window.fbq = pixel;
		window._fbq = pixel;
		pixel('consent', 'grant');
		pixel('set', 'autoConfig', false, pixelId);
		pixel('init', pixelId);
		const script = document.createElement('script');
		script.async = true;
		script.src = 'https://connect.facebook.net/en_US/fbevents.js';
		document.head.appendChild(script);
	}
}

export function trackMarketingEvent(
	name: string,
	eventId: string,
	data: Record<string, unknown> = {}
): void {
	if (!marketingAllowed()) return;
	loadMarketing();
	window.fbq?.('trackSingle', env.PUBLIC_META_PIXEL_ID, name, data, { eventID: eventId });
}

export function trackMarketingPage(): void {
	if (
		!marketingAllowed() ||
		/^\/(?:account|api|thanks|consent|dev)(?:\/|$)/.test(location.pathname)
	)
		return;
	if (lastPage === location.pathname) return;
	lastPage = location.pathname;
	trackMarketingEvent('PageView', crypto.randomUUID());
	if (/^\/(?:pricing|ppf-pattern-software)(?:\/|$)/.test(location.pathname)) {
		trackMarketingEvent('ViewContent', crypto.randomUUID(), {
			content_name: 'PPF Engine',
			content_ids: ['founder-presale'],
			content_type: 'product'
		});
	}
}
